import React, { useEffect, useState } from 'react';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getMaxWeight } from '../BunkeringUtils';
import { useBunkerContext } from 'src/context/BunkerContext';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkerChangeInputGrid } from './BunkerRob.styles';
import { TabLineButton } from 'src/styles/button.style';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';

interface Props {
    fuelTanks: Tank[];
    tankId: number;
    currentColumn: FuelRobColumn;
    fuelTransferFromRob: FuelROB | null;
    onTransferFuel: (currentColumn: FuelRobColumn, bunkeringGuid: string, fromTankId: number, toTankId: number, transferValue: number) => void;
    closeTransferModal: () => void;
    setFuelTransferFromRob: (value: FuelROB) => void;
}

const FuelTransfer: React.FC<Props> = ({
    fuelTanks,
    tankId,
    currentColumn,
    fuelTransferFromRob,
    onTransferFuel,
    closeTransferModal,
    setFuelTransferFromRob,
}) => {
    const { bunkerings } = useBunkerContext();
    const [transferValue, setTransferValue] = useState<number>(0);
    const [fuelTransferToTank, setFuelTransferToTank] = useState<Tank>(null);
    const [fuelTransferValueValid, setFuelTransferValueValid] = useState<boolean>(true);
    const [maxWeight, setMaxWeight] = useState<number>(0);
    const [bunkering, setBunkering] = useState<Bunkering>();

    useEffect(() => {
        const _bunkering = bunkerings?.find((b) => b.bunkeringGuid === fuelTransferFromRob.bunkeringGuid);
        setBunkering(_bunkering);
        if (!fuelTransferToTank || !fuelTransferFromRob) return;
        const litreWeight = _bunkering ? _bunkering?.bdnLiterWeight : 1;
        setMaxWeight(Math.min(fuelTransferFromRob?.rob ?? 0, getMaxWeight(fuelTransferToTank, litreWeight)));
    }, [fuelTransferToTank, bunkerings, fuelTransferFromRob]);

    const handleChangeTransferValue = (changeObject: InputChangeData) => {
        const value = +changeObject.event.target.value;
        setFuelTransferValueValid(value <= fuelTransferFromRob.rob && value <= maxWeight && value >= 0);
        setTransferValue(value);
    };

    const handleCloseTransferModal = () => {
        setTransferValue(0);
        setFuelTransferFromRob(null);
        setFuelTransferToTank(null);
        closeTransferModal();
    };

    return (
        <OdfjellPanel
            headerTitle={`Transfer fuel ${bunkering?.bunkeringReference}`}
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.LARGE_WIDTH}
            actionButtonText="Confirm"
            onAction={() => {
                onTransferFuel(currentColumn, fuelTransferFromRob.bunkeringGuid, tankId, fuelTransferToTank.tankId, transferValue);
                handleCloseTransferModal();
            }}
            isActionDisabled={!fuelTransferValueValid || !fuelTransferToTank}
            onCancel={() => handleCloseTransferModal()}
            showCancelButton={true}
        >
            {
                <BunkerChangeInputGrid>
                    <div>From tank:</div>
                    <div>{fuelTanks.find((tank) => tank.tankId === tankId)?.tankName}</div>
                    <div>To Tank:</div>
                    <OdfjellInputComponent
                        type="select"
                        options={fuelTanks.filter((tank) => tank.tankId !== tankId).map((tank) => tank.tankName)}
                        onChange={(changeObject) =>
                            setFuelTransferToTank(fuelTanks.find((tank) => tank.tankName === changeObject.event.target.value))
                        }
                        selectDefaultOption="Select tank"
                    />
                    <div>Transfer quantity:</div>
                    <OdfjellInputComponent
                        type="number"
                        name="transferValue"
                        value={transferValue}
                        min={0}
                        max={maxWeight}
                        onChange={(changeObject) => handleChangeTransferValue(changeObject)}
                        unit="MT"
                        validationInTooltip
                        isReadOnly={!fuelTransferToTank}
                    ></OdfjellInputComponent>
                    <div></div>
                    <TabLineButton
                        style={{ width: '60px' }}
                        onClick={() =>
                            handleChangeTransferValue({
                                event: { target: { value: maxWeight } },
                            })
                        }
                        disabled={!fuelTransferToTank}
                    >
                        Max
                    </TabLineButton>
                </BunkerChangeInputGrid>
            }
        </OdfjellPanel>
    );
};

export default FuelTransfer;
